import { motion } from 'framer-motion'
import {
  Gift,
  Star,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Info,
  Zap,
  Trophy
} from 'lucide-react'
import { useMemo } from 'react'
import { Button } from '../ui'
import RewardEligibilityTimer from './RewardEligibilityTimer'
import type { UserLoyalty } from '../../types/loyalty'
import { getTimeRemainingInEligibilityWindow } from '../../types/loyalty'

interface EligibilityStatusBannerProps {
  userLoyalty: UserLoyalty
  onViewDashboard?: () => void
  className?: string
  variant?: 'full' | 'compact'
}

const EligibilityStatusBanner = ({
  userLoyalty,
  onViewDashboard,
  className = '',
  variant = 'full'
}: EligibilityStatusBannerProps) => {
  const timeRemaining = useMemo(() =>
    getTimeRemainingInEligibilityWindow(userLoyalty.joinedAt),
    [userLoyalty.joinedAt]
  )

  const eligibilityStatus = useMemo(() => {
    if (timeRemaining.expired) return 'expired'
    if (timeRemaining.days <= 7) return 'critical'
    if (timeRemaining.days <= 30) return 'warning'
    return 'active'
  }, [timeRemaining])

  const unlockedWithRewards = userLoyalty.achievements.filter(
    a => a.isUnlocked && a.rewardType === 'frame_and_rewards'
  ).length

  const unlockedFrameOnly = userLoyalty.achievements.filter(
    a => a.isUnlocked && a.rewardType === 'frame_only'
  ).length

  const totalUnlocked = unlockedWithRewards + unlockedFrameOnly

  // Don't show banner if user has been around for too long and has no achievements
  if (timeRemaining.expired && totalUnlocked === 0) {
    return null
  }

  const getBannerConfig = () => {
    switch (eligibilityStatus) {
      case 'expired':
        return {
          gradient: 'from-gray-100 to-gray-200',
          border: 'border-gray-300',
          icon: AlertTriangle,
          iconColor: 'text-gray-600',
          title: 'Período de elegibilidad expirado',
          subtitle: 'Solo puedes conseguir marcos visuales',
          textColor: 'text-gray-700',
          accentColor: 'text-gray-600'
        }
      case 'critical':
        return {
          gradient: 'from-red-50 to-orange-50',
          border: 'border-red-200',
          icon: AlertTriangle,
          iconColor: 'text-red-600',
          title: '¡Últimos días para recompensas!',
          subtitle: 'Aprovecha ahora para conseguir recompensas físicas',
          textColor: 'text-red-900',
          accentColor: 'text-red-600'
        }
      case 'warning':
        return {
          gradient: 'from-orange-50 to-yellow-50',
          border: 'border-orange-200',
          icon: Clock,
          iconColor: 'text-orange-600',
          title: 'Tiempo limitado para recompensas',
          subtitle: 'Menos de un mes para conseguir recompensas físicas',
          textColor: 'text-orange-900',
          accentColor: 'text-orange-600'
        }
      default:
        return {
          gradient: 'from-green-50 to-emerald-50',
          border: 'border-green-200',
          icon: Gift,
          iconColor: 'text-green-600',
          title: '¡Ventana de recompensas activa!',
          subtitle: 'Consigue recompensas físicas al completar logros',
          textColor: 'text-green-900',
          accentColor: 'text-green-600'
        }
    }
  }

  const config = getBannerConfig()
  const BannerIcon = config.icon

  // Compact variant
  if (variant === 'compact') {
    return (
      <motion.div
        className={`flex items-center justify-between p-4 rounded-lg bg-gradient-to-r ${config.gradient} border ${config.border} ${className}`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center space-x-3">
          <BannerIcon className={`w-5 h-5 ${config.iconColor}`} />
          <div>
            <h4 className={`font-semibold text-sm ${config.textColor}`}>
              {config.title}
            </h4>
            {!timeRemaining.expired && (
              <RewardEligibilityTimer
                registrationDate={userLoyalty.joinedAt}
                variant="compact"
                showIcon={false}
                className="mt-1"
              />
            )}
          </div>
        </div>

        {onViewDashboard && (
          <Button
            onClick={onViewDashboard}
            variant="ghost"
            size="sm"
            className={config.accentColor}
          >
            Ver Dashboard
          </Button>
        )}
      </motion.div>
    )
  }

  // Full variant
  return (
    <motion.div
      className={`rounded-xl bg-gradient-to-br ${config.gradient} border ${config.border} overflow-hidden ${className}`}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <motion.div
              className={`p-3 rounded-full bg-white/50 backdrop-blur-sm`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
            >
              <BannerIcon className={`w-6 h-6 ${config.iconColor}`} />
            </motion.div>
            <div>
              <h3 className={`text-xl font-bold ${config.textColor}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${config.accentColor}`}>
                {config.subtitle}
              </p>
            </div>
          </div>

          {onViewDashboard && (
            <Button
              onClick={onViewDashboard}
              variant="outline"
              size="sm"
              className={`border-current ${config.textColor} hover:bg-white/10`}
            >
              Ver Dashboard
            </Button>
          )}
        </div>

        {/* Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Achievements Status */}
          <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
            <div className={`text-2xl font-bold ${config.textColor}`}>
              {totalUnlocked}
            </div>
            <div className={`text-sm ${config.accentColor} flex items-center justify-center space-x-1`}>
              <Trophy className="w-4 h-4" />
              <span>Logros desbloqueados</span>
            </div>
          </div>

          {/* Rewards Status */}
          <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
            <div className={`text-2xl font-bold ${config.textColor}`}>
              {unlockedWithRewards}
            </div>
            <div className={`text-sm ${config.accentColor} flex items-center justify-center space-x-1`}>
              <Gift className="w-4 h-4" />
              <span>Con recompensas</span>
            </div>
          </div>

          {/* Frame Only Status */}
          <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4 text-center">
            <div className={`text-2xl font-bold ${config.textColor}`}>
              {unlockedFrameOnly}
            </div>
            <div className={`text-sm ${config.accentColor} flex items-center justify-center space-x-1`}>
              <Star className="w-4 h-4" />
              <span>Solo marco</span>
            </div>
          </div>
        </div>

        {/* Time Remaining or Expiry Message */}
        {!timeRemaining.expired ? (
          <RewardEligibilityTimer
            registrationDate={userLoyalty.joinedAt}
            variant="banner"
            className="mb-4"
          />
        ) : (
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3">
              <Info className="w-5 h-5 text-gray-600" />
              <div>
                <h4 className="font-semibold text-gray-900">
                  Período de elegibilidad completado
                </h4>
                <p className="text-sm text-gray-600">
                  Los nuevos logros solo incluirán marcos visuales.
                  Los logros ya conseguidos con recompensas se mantienen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Items */}
        <div className="space-y-3">
          {eligibilityStatus === 'active' && (
            <div className="bg-white/40 backdrop-blur-sm rounded-lg p-4">
              <h4 className={`font-semibold ${config.textColor} mb-2 flex items-center`}>
                <Zap className="w-4 h-4 mr-2" />
                Cómo conseguir recompensas:
              </h4>
              <ul className={`text-sm ${config.accentColor} space-y-1`}>
                <li>• Participa en sorteos para ganar puntos de lealtad</li>
                <li>• Alcanza los niveles de logros (Explorer: 500pts, Hunter: 1500pts, etc.)</li>
                <li>• Reclama tus recompensas físicas desde el dashboard</li>
                <li>• ¡Aprovecha tu ventana de {timeRemaining.days} días restantes!</li>
              </ul>
            </div>
          )}

          {eligibilityStatus === 'critical' && (
            <div className="bg-red-100/50 backdrop-blur-sm rounded-lg p-4">
              <h4 className={`font-semibold ${config.textColor} mb-2 flex items-center`}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                ¡Actúa rápido!
              </h4>
              <p className={`text-sm ${config.accentColor}`}>
                Solo tienes {timeRemaining.days} días para conseguir puntos y desbloquear logros
                con recompensas físicas. ¡No pierdas esta oportunidad única!
              </p>
            </div>
          )}

          {eligibilityStatus === 'warning' && (
            <div className="bg-orange-100/50 backdrop-blur-sm rounded-lg p-4">
              <h4 className={`font-semibold ${config.textColor} mb-2 flex items-center`}>
                <Clock className="w-4 h-4 mr-2" />
                Tiempo limitado
              </h4>
              <p className={`text-sm ${config.accentColor}`}>
                Te quedan {timeRemaining.days} días para conseguir recompensas físicas.
                Después solo podrás obtener marcos visuales.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default EligibilityStatusBanner